import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import '@fontsource-variable/inter/wght.css'
import './styles.css'
import { routeTree } from './routeTree.gen'
import { ApiError } from './lib/api'
import { ErrorBox, Spinner } from './components/ui'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2 },
  },
  queryCache: new QueryCache({
    // Session expired mid-use: send them to sign in, then back to where they were.
    onError: (e) => {
      if (e instanceof ApiError && e.status === 401 && !location.pathname.startsWith('/login'))
        router.navigate({ to: '/login', search: { next: location.pathname + location.search } })
    },
  }),
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0, // let TanStack Query decide freshness
  scrollRestoration: true,
  defaultPendingComponent: () => <div className="grid min-h-[50vh] place-items-center text-brand"><Spinner className="size-7" /></div>,
  defaultErrorComponent: ({ error, reset }) => <div className="p-6"><ErrorBox error={error} onRetry={reset} /></div>,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
